import { useId, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useCreateService } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface CreateServiceModalProps {
  open: boolean;
  fleetId: string;
  projectId: string;
  onClose: () => void;
}

const DEFAULT_DESIRED_COUNT = "1";

/**
 * Defines a deployable service within a project — an image and a desired
 * instance count. Mirrors `createServiceRequest` (server/internal/
 * projectsvc/http.go) exactly: this client only offers the image path here
 * (not `composeRef`, which names an existing Stack), and env/secretRefs
 * aren't set at creation — edit those from the service detail page.
 *
 * Creating a service does NOT deploy it — deploying isn't implemented yet
 * server-side (`TriggerDeployHandler` answers 501); see ServiceDetailPage
 * for how that's rendered honestly.
 */
export function CreateServiceModal({ open, fleetId, projectId, onClose }: CreateServiceModalProps) {
  const nameId = useId();
  const imageId = useId();
  const desiredCountId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const mutation = useCreateService(fleetId, projectId);

  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [desiredCount, setDesiredCount] = useState(DEFAULT_DESIRED_COUNT);

  function handleClose() {
    setName("");
    setImage("");
    setDesiredCount(DEFAULT_DESIRED_COUNT);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    const parsedCount = Number.parseInt(desiredCount, 10);
    mutation.mutate(
      {
        name: submittedName,
        image: image.trim(),
        desiredCount: parsedCount,
      },
      {
        onSuccess: (created) => {
          pushToast({ title: `Service "${created.name}" created.`, tone: "success" });
          handleClose();
          navigate(`/projects/${projectId}/services/${created.id}`);
        },
      },
    );
  }

  const parsedCount = Number.parseInt(desiredCount, 10);
  const canSubmit =
    name.trim().length > 0 &&
    image.trim().length > 0 &&
    Number.isInteger(parsedCount) &&
    parsedCount >= 0 &&
    !mutation.isPending;

  return (
    <Modal open={open} title="New service" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't create this service."}
          </p>
        )}

        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={63}
          />
        </label>

        <label className="form-field" htmlFor={imageId}>
          <span>Image</span>
          <input
            id={imageId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="ghcr.io/acme/api:1.0.0"
            value={image}
            onChange={(e) => setImage(e.target.value)}
            required
          />
        </label>

        <label className="form-field" htmlFor={desiredCountId}>
          <span>Desired count</span>
          <input
            id={desiredCountId}
            type="number"
            min={0}
            value={desiredCount}
            onChange={(e) => setDesiredCount(e.target.value)}
            required
          />
          <span className="form-field__hint">The target instance/replica count used for capacity management.</span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Creating…" : "Create service"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
